import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ShoppingCart,
  Mail,
  Phone,
  Wine,
  CreditCard,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  RefreshCcw,
} from 'lucide-react';
import { formatDate } from '../../lib/admin/utils';
import { toast } from '../../hooks/use-toast';

// Status → display config
const STATUS_META = {
  pending:   { label: 'Pending',   className: 'bg-gray-200 text-gray-800' },
  confirmed: { label: 'Confirmed', className: 'bg-blue-100 text-blue-800' },
  paid:      { label: 'Paid',      className: 'bg-green-100 text-green-800' },
  completed: { label: 'Completed', className: 'bg-emerald-200 text-emerald-900' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
  refunded:  { label: 'Refunded',  className: 'bg-purple-100 text-purple-800' },
};

// Same map the backend uses (for the UI side)
const ALLOWED = {
  pending:   ['confirmed', 'cancelled'],
  confirmed: ['paid', 'cancelled', 'completed'],
  paid:      ['completed', 'refunded'],
  completed: ['refunded'],
  cancelled: [],
  refunded:  [],
};

// Primary "happy path" button per status
const PRIMARY_ACTION = {
  pending:   { next: 'confirmed', label: 'Confirm Order',     Icon: CheckCircle2 },
  confirmed: { next: 'completed', label: 'Mark Completed',    Icon: CheckCircle2 },
  paid:      { next: 'completed', label: 'Mark Completed',    Icon: CheckCircle2 },
};

const OrderCard = ({ order, onStatusChange, onSendPaymentLink }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [picker, setPicker] = useState('');

  const status = order.status || 'pending';
  const meta = STATUS_META[status] || STATUS_META.pending;
  const allowed = ALLOWED[status] || [];
  const primary = PRIMARY_ACTION[status];

  const handleAction = async (next) => {
    if (!next || busy) return;
    setBusy(true);
    try {
      await onStatusChange(order.orderId, next);
    } finally {
      setBusy(false);
    }
  };

  const handleSendLink = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onSendPaymentLink(order.orderId);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!order.paymentLink) return;
    try {
      await navigator.clipboard.writeText(order.paymentLink);
      setCopied(true);
      toast({ title: 'Copied!', description: 'Payment link copied to clipboard.' });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <Card
      className="hover:shadow-lg transition-shadow border-l-4 border-l-[#FDB913]"
      data-testid="order-card"
    >
      <CardContent className="pt-6">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <ShoppingCart className="w-5 h-5 text-[#FDB913]" />
              <span className="font-semibold text-lg">
                {order.firstName} {order.lastName}
              </span>
              <Badge className={meta.className} data-testid={`order-status-${order.orderId}`}>
                {meta.label}
              </Badge>
              {order.referralCode && (
                <Badge className="bg-purple-100 text-purple-800">
                  Referral: {order.referralCode}
                </Badge>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-500" />
                <a href={`mailto:${order.email}`} className="text-blue-600 hover:underline">
                  {order.email}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-500" />
                <a href={`tel:${order.phone}`} className="text-blue-600 hover:underline">
                  {order.phone}
                </a>
              </div>
              <div><span className="font-semibold">Preferred Contact:</span> {order.preferredContact}</div>
              <div>
                <span className="font-semibold">Yeast Activation:</span>{' '}
                {order.yeastActivation === 'consent' ? 'AGCO Consent (Lokal activates)' : 'In-Person'}
              </div>
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-[#FDB913] mb-2">
              ${Number(order.totalAmount || 0).toFixed(2)}
            </div>
            {order.notes && (
              <div className="text-sm mb-2">
                <span className="font-semibold">Notes:</span>
                <p className="text-gray-600 mt-1">{order.notes}</p>
              </div>
            )}
            <div className="text-xs text-gray-500">Submitted: {formatDate(order.createdAt)}</div>
          </div>
        </div>

        {/* Wine selections */}
        <div className="border-t pt-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Wine className="w-4 h-4 text-gray-600" />
            <span className="font-semibold text-sm">Wines Ordered:</span>
          </div>
          <div className="space-y-2">
            {(order.wineSelections || []).map((wine, wIdx) => (
              <div
                key={`${wine.wineName}-${wIdx}`}
                className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded-md text-sm"
              >
                <div>
                  <span className="font-medium">{wine.wineName}</span>
                  <span className="text-gray-500"> · {wine.category}</span>
                  <span className="text-gray-500">
                    {' '}· {wine.batchSize === 'half' ? 'Half Batch (15 btl)' : 'Full Batch (30 btl)'}
                  </span>
                </div>
                <div className="font-semibold">
                  {wine.quantity}× ${wine.price} = ${(wine.quantity * wine.price).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment link bar */}
        {order.paymentLink && (
          <div className="border-t pt-3 mb-4">
            <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
              <CreditCard className="w-4 h-4 text-blue-600" />
              Stripe Payment Link
              {order.paymentStatus && (
                <Badge variant="secondary" className="ml-2">{order.paymentStatus}</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <a
                href={order.paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline truncate flex-1"
              >
                {order.paymentLink}
              </a>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid={`copy-link-${order.orderId}`}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}

        {/* Action bar */}
        <div className="border-t pt-3 flex flex-wrap items-center gap-2">
          {primary && (
            <Button
              size="sm"
              onClick={() => handleAction(primary.next)}
              disabled={busy}
              data-testid={`btn-primary-${order.orderId}`}
              className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900"
            >
              <primary.Icon className="w-4 h-4 mr-1.5" />
              {primary.label}
            </Button>
          )}

          {status === 'confirmed' && (
            <Button
              size="sm"
              onClick={handleSendLink}
              disabled={busy}
              data-testid={`btn-send-payment-${order.orderId}`}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <CreditCard className="w-4 h-4 mr-1.5" />
              {order.paymentLink ? 'Resend Payment Link' : 'Send Payment Link'}
            </Button>
          )}

          {allowed.includes('cancelled') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAction('cancelled')}
              disabled={busy}
              data-testid={`btn-cancel-${order.orderId}`}
              className="text-red-600 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              Cancel
            </Button>
          )}

          {allowed.includes('refunded') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAction('refunded')}
              disabled={busy}
              data-testid={`btn-refund-${order.orderId}`}
              className="text-purple-700 hover:bg-purple-50"
            >
              <RefreshCcw className="w-4 h-4 mr-1.5" />
              Refund
            </Button>
          )}

          {/* Manual status picker (advanced) */}
          {allowed.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-gray-500">Set status:</span>
              <Select
                value={picker}
                onValueChange={(value) => {
                  setPicker('');
                  handleAction(value);
                }}
              >
                <SelectTrigger
                  className="h-8 w-[160px] text-xs"
                  data-testid={`status-picker-${order.orderId}`}
                >
                  <SelectValue placeholder="Choose..." />
                </SelectTrigger>
                <SelectContent>
                  {allowed.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_META[s]?.label || s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {allowed.length === 0 && (
            <span className="ml-auto text-xs text-gray-400 italic">Terminal status — no further actions</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderCard;
